import { useEffect, useState } from "react";
import { Button, Flex, Icon, Stack, Text, useToast } from "@chakra-ui/react";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { format, formatISO, isFuture, isEqual } from "date-fns";
import ptBR from 'date-fns/locale/pt-BR';

import { useCart } from "../../hooks/cart";
import { Input } from "../Inputs/Input";
import useDebounce from "../../utils/useDebounce";

type AgendarFormData = {
	data: string;
	hora: string;
}
type EntregaData = {
	entrega_domingo: string;
	entrega_padrao: string;
	entrega_sabado: string;
}
type HorarioData ={
	hora: string;
	dia_util: string;
	fim_semana: string;
}
interface AgendarHorarioParams {
	horarios: HorarioData[];
	entrega: EntregaData;
}

const agendarFormSchema: yup.SchemaOf<AgendarFormData> = yup.object().shape({
	data: yup.string().required('Data obrigatório'),
	hora: yup.string().required('Hora obrigatória'),
});

const AgendarHorario: React.FC<AgendarHorarioParams> = ({
	horarios,
	entrega,
}) => {
	const { register, setValue, reset } = useForm<AgendarFormData>({
		resolver: yupResolver(agendarFormSchema)
	});

	const { cart, addCartHoraAgendada } = useCart();
	const toast = useToast();

	const [agendar, setAgendar] = useState(false);
	const [dataEntrega, setDataEntrega] = useState('');
	const [day, setDay] = useState(0);

	const debounceChange = useDebounce(isWeekday, 1000);

	useEffect(() => {
		setDay(new Date().getDay());
		if (cart.agendar && cart.agendamento.data !== '') {
			setAgendar(false);
		}
	}, []);

	function isWeekday(date: string) {
		if (!date) {
			setDataEntrega('');
			return;
		};

		const today = formatISO(new Date(), { representation: 'date' });
		const equal = isEqual(new Date(date), new Date(today));
		const future = isFuture(new Date(date));

		if (!equal && !future) {
			setValue("data", "");
			setDataEntrega('');

			toast({
				title: 'Dia da Entrega.',
				description: 'Esta data já passou!',
				status: 'error',
				duration: 5000,
				position: 'top-right',
				isClosable: true,
				containerStyle: {
					marginTop: '20'
				}
			});
			return;
		}

		const dia = new Date(date).getDay();
		setDay(dia);

		if (entrega.entrega_sabado === 'N' && dia === 0) {
			setValue("data", "");
			setDataEntrega('');

			toast({
				title: 'Dia da Entrega.',
				description: 'Não entregamos no sabádo!',
				status: 'error',
				duration: 5000,
				position: 'top-right',
				isClosable: true,
				containerStyle: {
					marginTop: '20'
				}
			});

			return;
		} else if (entrega.entrega_domingo === 'N' && dia === 6) {
			setValue("data", "");
			setDataEntrega('');

			toast({
				title: 'Dia da Entrega.',
				description: 'Não entregamos no domingo!',
				status: 'error',
				duration: 5000,
				position: 'top-right',
				isClosable: true,
				containerStyle: {
					marginTop: '20'
				}
			});

			return;
		}

		// const data = new Date(date);
		// const f = format(new Date(data), 'yyyy-MM-dd', { locale: ptBR });

		setDataEntrega(date);
	};

	function horizontalScroll(e: any) {
		const container = document.getElementById('horarios');
		const containerScrollPosition = document.getElementById('horarios')
			?.scrollLeft;
		container?.scrollTo({
			top: 0,
			left: containerScrollPosition + e.deltaY,
			// behaviour: 'smooth', // if you want smooth scrolling
		});
	};

	function handleMenuScroll(sentido: string) {
		const aux = document.getElementById('horarios');

		if (aux) {
			if (sentido === 'right') {
				aux.scrollLeft += 150;
			} else {
				aux.scrollLeft -= 150;
			}
		}
	};

	return (
		<Flex flexDir="column" mt="6">
			{/* ENTREGA / AGENDAR */}
			<Stack direction="row" spacing="4">
				<Text
					as="button"
					fontSize={["18", "20"]}
					fontWeight="500"
					color={!cart.agendar ? 'blue.500' : 'gray.400'}
					pb="2"
					borderBottomWidth={2}
					borderBottomColor={!cart.agendar ? 'blue.500' : 'gray.400'}
					onClick={() => {
						addCartHoraAgendada({} as AgendarFormData, false);
						setAgendar(false);
						reset();
					}}
				>
					Entrega
				</Text>

				<Text
					as="button"
					fontSize={["18", "20"]}
					fontWeight="500"
					color={cart.agendar ? 'blue.500' : 'gray.400'}
					pb="2"
					borderBottomWidth={2}
					borderBottomColor={cart.agendar ? 'blue.500' : 'gray.400'}
					onClick={() => {
						addCartHoraAgendada({} as AgendarFormData, true);
						setAgendar(true);
					}}
				>
					Agendar
				</Text>
			</Stack>

			{!agendar ? (
				<Flex
					justify="space-between"
					my="2"
					px="4"
					py="2"
					borderWidth={1}
					borderColor="blue.500"
					borderRadius={8}
				>
					<Flex flexDir="column">
						<Text fontSize={["13", "15"]} fontWeight="500" color="blue.500">
							{cart.agendar ? 'Horário agendado' : 'Padrão'}
						</Text>

						<Text fontSize={["14", "15"]} fontWeight="500" color="gray.400">
							{cart.agendar && cart.agendamento.data
								? `${format(new Date(cart.agendamento.data).setDate(new Date(cart.agendamento.data).getDate() + 1),
									"EEEE', dia' dd 'de' MMMM 'de' yyyy 'às' ",
									{ locale: ptBR },
								)} ${cart.agendamento?.hora}h`
								: 'Até 5 dias uteis'
							}
						</Text>
						<Text fontSize={["14", "15"]} fontWeight="500">R$ 0,00</Text>
					</Flex>

					{cart.agendar && (
						<Flex flexDir="column" justify="flex-end" onClick={() => setAgendar(true)}>
							<Text
								as="button"
								fontSize={["13", "16"]}
								fontWeight={["600", "500"]}
								color="blue.500"
							>
								Editar
							</Text>
						</Flex>
					)}
				</Flex>
			) : (
				<Flex
					id="endereco"
					my="4"
					flexDir="column"
				>
            <Flex w="100%" justify="space-between" onClick={() => setAgendar(false)}>
              <Text fontSize={18} fontWeight={500}>Data para entrega</Text>

              <Text
                as="button"
                fontSize={["13", "16"]}
                fontWeight={["600", "500"]}
                color="red.500"
              >
                Cancelar
              </Text>
            </Flex>
					<Input
						// label="Data para entrega"
						type="date"
						{...register("data")}
						onChange={e => debounceChange(e.target.value)}
					/>

					<Flex
						justify="center"
						align="center"
						my="4"
						p="2"
						borderWidth={1}
						borderColor="blue.500"
						borderRadius={8}
					>
						<Flex
							as="button"
							bg="gray.50"
							justify="center"
							align="center"
							py="4"
							mr="2"
							borderRadius={8}
							onClick={() => handleMenuScroll('left')}
						>
							<Icon as={FiChevronLeft} fontSize="24" />
						</Flex>

						<Flex
							id="horarios"
							scrollBehavior="smooth"
							overflowX="scroll"
							onWheel={horizontalScroll}
							w={["lg", "lg", "3xl", "sm"]}
						>
							<Stack direction="row" spacing="2" p="2">
							{horarios.map((horario) => {
									return (
										((day && day > 0 && day < 6 && horario.dia_util === 'S') ||
										(day === 0 && entrega.entrega_sabado === 'S' && horario.fim_semana === 'S') ||
										(day === 6 && entrega.entrega_domingo === 'S' && horario.fim_semana === 'S')) &&  (
											<Button
												key={horario.hora}
												bg="white"
												p="2"
												borderWidth={1}
												borderColor="gray.200"
												borderRadius="8"
												_hover={{
													boxShadow: "1px 2px 2px 2px rgba(0, 0, 0, 0.2)"
												}}
												isDisabled={dataEntrega === '' ? true : false}
												onClick={() => {
													setAgendar(false);
													addCartHoraAgendada({ data: dataEntrega, hora: horario.hora }, true);
												}}
											>
												<Text fontSize={["15", "16"]} fontWeight="500">{horario.hora}</Text>
											</Button>
										)
									)
								})}
							</Stack>
						</Flex>

						<Flex
							as="button"
							bg="gray.50"
							justify="center"
							align="center"
							py="4"
							ml="2"
							borderRadius={8}
							onClick={() => handleMenuScroll('right')}
						>
							<Icon as={FiChevronRight} fontSize="24" />
						</Flex>
					</Flex>
				</Flex>
			)}
		</Flex>
	)
}

export default AgendarHorario;
